/**
 * Which surface the Review tab shows, as one ordered decision.
 *
 * The order is the whole point, and it was wrong. `isFailed` reads the
 * flight's LAST outcome, so while a NEW generation is in flight both "failed"
 * and "generating" are true at once. Checking failure first meant a fresh
 * Generate rendered the previous run's "Schedule Generation Failed" for the
 * entire synchronous POST — tens of seconds on a large flight, with the modal
 * closed and nothing else on screen to say the click had registered.
 *
 * Extracted so the precedence is testable directly. The page calls this
 * function; a test asserting on a re-implementation of it would have passed
 * against the broken order.
 */
export type ReviewSurface =
  | 'success'
  | 'generating'
  | 'error'
  | 'partially-generated'
  | 'default'

export interface ReviewSurfaceState {
  /** Post-generation success animation is playing or just became due. */
  showSuccess: boolean
  /**
   * A generation is under way — including a synchronous POST /generate that
   * has not returned yet, when nothing is polling and the flight still
   * reports its previous status.
   */
  isGenerating: boolean
  isFailed: boolean
  isCancelled: boolean
  /** A generation request threw and its error has not been cleared. */
  hasGenerateError: boolean
  isPartiallyGenerated: boolean
  /** The admin chose to review the completed divisions of a partial flight. */
  reviewingPartialGames: boolean
}

export function resolveReviewSurface(state: ReviewSurfaceState): ReviewSurface {
  // Generating outranks EVERY cached outcome — success included.
  //
  // The first fix here only demoted `isFailed`, which left the mirror-image
  // bug: re-generating a flight that had SUCCEEDED showed "Schedule
  // Generated · Preparing your results..." while the new POST was still
  // pending in the network tab, because `isGenerated` was still true from the
  // previous run. Both stale terminal states have to lose to an attempt that
  // is actually in flight.
  if (state.isGenerating) {
    return 'generating'
  }
  if (state.showSuccess) {
    return 'success'
  }
  if (state.isFailed || state.isCancelled || state.hasGenerateError) {
    return 'error'
  }
  if (state.isPartiallyGenerated && !state.reviewingPartialGames) {
    return 'partially-generated'
  }
  return 'default'
}
